import React, { Suspense } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { isTokenValid } from "./utils/checkTokenValidity";
import { useEffect } from "react";
import Welcome from "./pages/Welcome";
import Header from "./components/Header/Header";
import SideBar from "./components/Sidebar";
import Footer from "./components/Footer";
import CreateUser from "./pages/CreateUser";
import Login from "./pages/Login";
import HomePage from "./pages/HomePage";
import StudentWelcome from "./pages/StudentWelcome";
import ProfessorWelcome from "./pages/ProfessorWelcome";
import StudentDashboard from "./pages/StudentDashboard";
import ProfessorDashboard from "./pages/ProfessorDashboard";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./context/AuthContext";
import ProfessorPositions from "./pages/ProfessorPositionsPage";
import ProfilePage from "./pages/ProfilePage";
import SavedPage from "./pages/SavedPage";
import StudentMatching from "./pages/StudentMatchingPage";
import ProfessorPoolPage from "./pages/ProfessorPoolPage";
import Notifications from "./pages/Notifications";
import DirectMessages from "./pages/DirectMessages";
import Blog from "./pages/BlogPage";
import BlogPost from "./components/Blog/BlogPost";
import NotFound from "./pages/NotFound";
import MeetTeam from "./pages/MeetTeam";
import Contact from "./pages/Contact";
import SelectRole from "./pages/SelectRole";
import AuthSuccess from "./components/AuthSuccess";
// import AdminDashboard from './components/AdminDashboard/AdminDashboard';
import AdminDashboard from "./pages/AdminPanel";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import FeedbackForm from "./pages/FeedbackForm";
import ApplicationPage from "./pages/ApplicationPage";
import ProfessorApplicationsPage from "./pages/ProfApplicationsPage";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
// import Pricing from './pages/Pricing';
import EmailVerification from "./pages/EmailVerification";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import MessageBoard from "./pages/MessageBoard";
import PositionsAdmin from "./pages/PositionsAdmin";
import PositionViewPage from "./pages/PositionViewPage";
import AccountSettings from "./pages/AccountSettings";
import BillingSuccess from "./pages/BillingSuccess";

// Lazy load RoleSelection component
const RoleSelection = React.lazy(() => import("./pages/RoleSelection"));

function App() {
  const location = useLocation();

  const pagesWithoutSidebar = [
    "/",
    "/create-user",
    "/login",
    "/email-verification",
    "/forgot-password",
    "/reset-password",
    "/HomePage",
    "/MeetTeam",
    // '/pricing',
  ];

  // Authentication check
  const isLoggedIn = () => {
    const user = localStorage.getItem("user");
    return user !== null;
  };

  // const shouldShowSidebar = !pagesWithoutSidebar.includes(location.pathname);
  const conditionalSidebarPages = [
    "/welcome",
    "/blog",
    "/welcome-page",
    "/Contact",
  ];
  // Check if current path is a position view page
  const isPositionViewPage = location.pathname.startsWith("/position/");
  const shouldShowSidebar =
    !pagesWithoutSidebar.includes(location.pathname) &&
    (!conditionalSidebarPages.includes(location.pathname) || isLoggedIn()) &&
    (!isPositionViewPage || isLoggedIn());

  const pagesWithoutFooter = ["/", "/welcome-page", "/blog"];
  const shouldShowFooter = !pagesWithoutFooter.includes(location.pathname);

  return (
    <AuthProvider>
      <div className="min-h-screen flex">
        {/* Sidebar - Fixed position */}
        {shouldShowSidebar && <SideBar />}

        {/* Main content area */}
        <div className={`flex-1 ${shouldShowSidebar ? "ml-[70px]" : ""}`}>
          {/* Header */}
          <Header />

          {/* Main content */}
          <main>
            <Routes>
              <Route path="/" element={<Welcome />} />
              <Route path="/welcome-page" element={<Welcome />} />
              <Route
                path="/role-selection"
                element={
                  <Suspense
                    fallback={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100vh",
                          fontSize: "1.2rem",
                          color: "#3C6E71",
                        }}
                      >
                        Loading...
                      </div>
                    }
                  >
                    <RoleSelection />
                  </Suspense>
                }
              />
              <Route path="/create-user" element={<CreateUser />} />
              <Route path="/login" element={<Login />} />
              <Route
                path="/email-verification"
                element={<EmailVerification />}
              />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/HomePage" element={<HomePage />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/Blogs/:slug" element={<BlogPost />} />
              <Route path="/MeetTeam" element={<MeetTeam />} />
              <Route path="/saved" element={<SavedPage />} />
              <Route path="/Contact" element={<Contact />} />
              <Route path="/select-role" element={<SelectRole />} />
              <Route path="/auth/success" element={<AuthSuccess />} />
              <Route path="/feedback-form" element={<FeedbackForm />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route
                path="/analytics-dashboard"
                element={<AnalyticsDashboard />}
              />
              <Route
                path="/position/:positionId"
                element={<PositionViewPage />}
              />
              {/* <Route path="/pricing" element={<Pricing />} /> */}
              <Route
                path="/applications"
                element={
                  <ProtectedRoute allowedRoles={["student"]}>
                    <ApplicationPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/apply/:positionId" element={<ApplicationPage />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/positions"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <PositionsAdmin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/professor/applications/:positionId"
                element={
                  <ProtectedRoute allowedRoles={["professor"]}>
                    <ProfessorApplicationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student-welcome"
                element={
                  <ProtectedRoute allowedRoles={["student"]}>
                    <StudentWelcome />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/professor-welcome"
                element={
                  <ProtectedRoute allowedRoles={["professor"]}>
                    <ProfessorWelcome />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/professor-dashboard"
                element={
                  <ProtectedRoute allowedRoles={["professor"]}>
                    <ProfessorDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/professor-positions"
                element={
                  <ProtectedRoute allowedRoles={["professor"]}>
                    <ProfessorPositions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student-matching"
                element={
                  <ProtectedRoute allowedRoles={["student"]}>
                    <StudentMatching />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/professor-pool"
                element={
                  <ProtectedRoute allowedRoles={["student"]}>
                    <ProfessorPoolPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute allowedRoles={["student", "professor"]}>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student-dashboard"
                element={
                  <ProtectedRoute allowedRoles={["student"]}>
                    <StudentDashboard />
                  </ProtectedRoute>
                }
              />
                <Route
                    path="/account-settings"
                    element={
                        <AccountSettings/>
                    }
                />
                <Route
                    path="/billing-success"
                    element={
                        <BillingSuccess/>
                    }
                />
              <Route path="/message-board"
              element={
                  <ProtectedRoute allowedRoles={['student']}>
                    <MessageBoard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute allowedRoles={["student", "professor"]}>
                    <Notifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/direct-messages"
                element={
                  <ProtectedRoute allowedRoles={["student", "professor"]}>
                    <DirectMessages />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>

          {/* Footer */}
          {shouldShowFooter && <Footer />}
        </div>
      </div>
    </AuthProvider>
  );
}

export default App;
